import { defineCustomElements } from '@ionic/pwa-elements/loader'
import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { RecoilRoot } from 'recoil'
import { SWRConfig } from 'swr'

import { BothrsWebThemeProvider } from './@bothrs-theme/web'
import { ApplicationInsights, appInsights } from './ApplicationInsights'
import { environment } from './config/environment'
import { LayoutProvider } from './layout/providers/LayoutProvider'
import { FontAwesomeProvider } from './modules/app/providers/FontAwesomeProvider'
import { router } from './router'
import './setup'

import { RouterProvider } from 'react-router-dom'

import { swrRetryConfig } from '@/lib/swrRetry'
import { mixpanel } from '@/mixpanel'
import { theme as zeroheightTheme } from './theme-generated'

// Enable Mixpanel in production
if (environment.env === 'production') {
  mixpanel.enable()
}

// Global error handlers — only captures unhandled/breaking errors
if (appInsights) {
  window.addEventListener('error', (event) => {
    appInsights.trackException({
      exception: event.error ?? new Error(event.message),
      severityLevel: 3, // Error
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    appInsights.trackException({
      exception: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
      severityLevel: 3, // Error
    })
  })
}

// Call the element loader before the render call
defineCustomElements(window)

const container = document.querySelector('#react-root')

// I18n depends on URL
// ServerProvider depends on URL
if (container) {
  const root = createRoot(container)
  root.render(
    <StrictMode>
      <ApplicationInsights>
        <FontAwesomeProvider />
        <RecoilRoot>
          <HelmetProvider>
            <BothrsWebThemeProvider theme={zeroheightTheme} config={{ spacingBase: 4 }}>
              <I18nProvider i18n={i18n}>
                <SWRConfig value={swrRetryConfig}>
                  <LayoutProvider>
                    <RouterProvider router={router} />
                  </LayoutProvider>
                </SWRConfig>
              </I18nProvider>
            </BothrsWebThemeProvider>
          </HelmetProvider>
        </RecoilRoot>
      </ApplicationInsights>
    </StrictMode>,
  )
} else {
  console.error('React Root element not found!')
}
